import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Modalidade } from '@arena/shared/dominio';
import {
  ROTULO_MODALIDADE,
  zDefinicaoDeAlocacao,
  type HorarioSobreposto,
  type QuadroDeAlocacao,
} from '@arena/shared/schemas';
import { horaCurta } from '../agenda/formatos.js';
import { ErroDeApi, ErroDeRede, mensagemDoErro } from '../api/cliente.js';
import { turmasApi } from '../api/turmas.js';
import { Erro, EsqueletoDeCard } from '../ui/estados.jsx';
import { Icone } from '../ui/Icone.js';
import { CorpoDeModal, Modal } from '../ui/Modal.jsx';
import { Botao, Campo, Formulario, Nota, Vazio } from '../ui/primitivos.jsx';
import { useToast } from '../ui/Toast.jsx';
import { diasDaTurma } from './formatos.js';

/**
 * `#m-conflito` — a alocação fixa dos horários sobrepostos, pendência P1.
 *
 * O MECANISMO está pronto; QUAL alocação adotar é decisão do dono — e é
 * para essa escolha que esta tela existe. Os horários vêm detectados pelo
 * servidor (turmas de modalidades diferentes ocupando todas as quadras no
 * mesmo horário), não digitados. Para cada um, as opções são as formas de
 * distribuir as modalidades pelas quadras.
 *
 * A consequência é declarada antes do botão, como no protótipo: com as
 * quadras tomadas pelas turmas, o horário deixa de aceitar locação avulsa.
 */

interface Opcao {
  readonly chave: string;
  readonly rotulo: string;
  /** Modalidade → quadra. */
  readonly distribuicao: ReadonlyMap<Modalidade, string>;
}

function permutacoes<T>(itens: readonly T[], tamanho: number): T[][] {
  if (tamanho === 0) return [[]];
  return itens.flatMap((item, i) =>
    permutacoes([...itens.slice(0, i), ...itens.slice(i + 1)], tamanho - 1).map((resto) => [
      item,
      ...resto,
    ]),
  );
}

function opcoesDoHorario(horario: HorarioSobreposto, quadro: QuadroDeAlocacao): Opcao[] {
  const modalidades = [...new Set(horario.turmas.map((t) => t.modalidade))];
  const nome = new Map(quadro.quadras.map((q) => [q.id, q.nome]));
  return permutacoes(
    quadro.quadras.map((q) => q.id),
    modalidades.length,
  ).map((quadras) => {
    const distribuicao = new Map(modalidades.map((m, i) => [m, quadras[i] as string]));
    return {
      chave: quadras.join('|'),
      rotulo: modalidades
        .map(
          (m) =>
            `${ROTULO_MODALIDADE[m]} na ${nome.get(distribuicao.get(m) as string) ?? 'quadra'}`,
        )
        .join(' · '),
      distribuicao,
    };
  });
}

/** A opção que descreve o que está valendo: a alocação vigente, ou onde as turmas estão hoje. */
function opcaoAtual(horario: HorarioSobreposto, quadro: QuadroDeAlocacao, opcoes: Opcao[]): string {
  const vigente = (m: Modalidade) =>
    quadro.vigentes.find(
      (a) =>
        a.modalidade === m && a.hora === horario.hora && horario.diasSemana.includes(a.diaSemana),
    )?.quadra.id;
  const hoje = (m: Modalidade) => horario.turmas.find((t) => t.modalidade === m)?.quadraId;
  const casa = opcoes.find((o) =>
    [...o.distribuicao].every(([m, q]) => (vigente(m) ?? hoje(m)) === q),
  );
  return (casa ?? opcoes[0])?.chave ?? '';
}

const chaveDoHorario = (h: HorarioSobreposto) => `${h.diasSemana.join(',')}|${h.hora}`;

export function ModalDeConflito({ onFechar }: { onFechar: () => void }) {
  const consulta = useQuery({
    queryKey: ['turmas', 'alocacoes'],
    queryFn: () => turmasApi.quadroDeAlocacao(),
    retry: false,
  });

  if (consulta.isPending) {
    return (
      <Modal aberto onFechar={onFechar} titulo="Carregando os horários…">
        <CorpoDeModal>
          <EsqueletoDeCard />
        </CorpoDeModal>
      </Modal>
    );
  }
  if (consulta.isError) {
    const erro = consulta.error;
    return (
      <Modal
        aberto
        onFechar={onFechar}
        titulo="Alocação nos horários sobrepostos"
        largura="estreita"
      >
        <CorpoDeModal>
          <Erro
            tipo={
              erro instanceof ErroDeRede
                ? 'rede'
                : erro instanceof ErroDeApi && erro.ehPermissao
                  ? 'permissao'
                  : 'falha'
            }
            detalhe={mensagemDoErro(erro)}
          />
        </CorpoDeModal>
      </Modal>
    );
  }
  return <ConflitoAberto quadro={consulta.data} onFechar={onFechar} />;
}

function ConflitoAberto({ quadro, onFechar }: { quadro: QuadroDeAlocacao; onFechar: () => void }) {
  const avisar = useToast();
  const cliente = useQueryClient();

  const horarios = quadro.sobreposicoes;
  const opcoes = useMemo(
    () => new Map(horarios.map((h) => [chaveDoHorario(h), opcoesDoHorario(h, quadro)])),
    [horarios, quadro],
  );
  const [escolhas, setEscolhas] = useState<Record<string, string>>(() =>
    Object.fromEntries(
      horarios.map((h) => [
        chaveDoHorario(h),
        opcaoAtual(h, quadro, opcoes.get(chaveDoHorario(h)) ?? []),
      ]),
    ),
  );
  const [erro, setErro] = useState<string | null>(null);

  const definir = useMutation({
    mutationFn: () => {
      const alocacoes = horarios.flatMap((h) => {
        const opcao = opcoes
          .get(chaveDoHorario(h))
          ?.find((o) => o.chave === escolhas[chaveDoHorario(h)]);
        if (!opcao) return [];
        return h.diasSemana.flatMap((diaSemana) =>
          [...opcao.distribuicao].map(([modalidade, quadraId]) => ({
            diaSemana,
            hora: h.hora,
            duracaoMinutos: h.duracaoMinutos,
            modalidade,
            quadraId,
          })),
        );
      });
      return turmasApi.definirAlocacao(zDefinicaoDeAlocacao.parse({ alocacoes }));
    },
    onSuccess: () => {
      void cliente.invalidateQueries({ queryKey: ['turmas'] });
      void cliente.invalidateQueries({ queryKey: ['agenda'] });
    },
  });

  async function salvar() {
    setErro(null);
    try {
      const resultado = await definir.mutateAsync();
      avisar(
        resultado.turmasRealocadas > 0
          ? `Alocação fixada. ${resultado.turmasRealocadas === 1 ? 'Uma turma mudou' : `${resultado.turmasRealocadas} turmas mudaram`} de quadra e a grade das próximas semanas foi regerada.`
          : 'Alocação fixada. A grade já estava nas quadras escolhidas.',
      );
      onFechar();
    } catch (e) {
      setErro(mensagemDoErro(e));
    }
  }

  const descricao =
    horarios.length > 0
      ? `Pendência P1 · ${horarios.map((h) => `${diasDaTurma(h.diasSemana).toLowerCase()}, ${horaCurta(h.hora)}`).join(' e ')}`
      : 'Pendência P1';

  return (
    <Modal
      aberto
      onFechar={onFechar}
      marca={
        <span
          className="chip"
          style={{ background: 'var(--wa-bg)', color: 'var(--wa-fg)' }}
          aria-hidden="true"
        >
          <Icone nome="info" />
        </span>
      }
      titulo="Alocação nos horários sobrepostos"
      descricao={descricao}
      rodape={
        <>
          <Botao variante="fantasma" onClick={onFechar}>
            Decidir depois
          </Botao>
          {horarios.length > 0 && (
            <Botao onClick={() => void salvar()} disabled={definir.isPending}>
              {definir.isPending ? 'Regerando a grade…' : 'Salvar alocação'}
            </Botao>
          )}
        </>
      }
    >
      <CorpoDeModal>
        {horarios.length === 0 ? (
          <Vazio>
            Nenhum horário sobreposto agora: em nenhum horário as turmas de modalidades diferentes
            tomam todas as quadras ao mesmo tempo.
          </Vazio>
        ) : (
          <>
            <p className="sub">
              Nesses horários, modalidades diferentes acontecem ao mesmo tempo e consomem todas as
              quadras. Definindo a alocação fixa, a agenda passa a gerar a grade sozinha e a
              bloquear locação nesses horários.
            </p>
            <Formulario colunas={2} onSubmit={(e) => e.preventDefault()}>
              {horarios.map((h) => {
                const chave = chaveDoHorario(h);
                return (
                  <Campo
                    key={chave}
                    rotulo={`${diasDaTurma(h.diasSemana)} · ${horaCurta(h.hora)}`}
                    ajuda={
                      h.alocado ? 'Alocação já definida — salvar substitui.' : 'Ainda sem alocação.'
                    }
                  >
                    <select
                      value={escolhas[chave] ?? ''}
                      onChange={(e) =>
                        setEscolhas((atuais) => ({ ...atuais, [chave]: e.target.value }))
                      }
                    >
                      {(opcoes.get(chave) ?? []).map((o) => (
                        <option key={o.chave} value={o.chave}>
                          {o.rotulo}
                        </option>
                      ))}
                    </select>
                  </Campo>
                );
              })}
            </Formulario>
            <Nota>
              <b>Consequência.</b> Com as quadras ocupadas pelas turmas nesses horários, não há
              janela de locação avulsa neles — e uma locação já marcada ali impede a alocação até
              ser remarcada. A sugestão de horário de <b>Nova turma</b> mostra a ocupação dos
              horários vizinhos.
            </Nota>
          </>
        )}

        {quadro.vigentes.length > 0 && (
          <div>
            <div className="card-head" style={{ marginBottom: 10 }}>
              <h2>Alocações em vigor</h2>
            </div>
            <ul className="feed">
              {quadro.vigentes.map((a) => (
                <li key={a.id}>
                  <div>
                    <b>
                      {diasDaTurma([a.diaSemana])} · {horaCurta(a.hora)}
                    </b>
                    <small>
                      {ROTULO_MODALIDADE[a.modalidade]} na {a.quadra.nome} · desde{' '}
                      {a.vigenteDesde.slice(8, 10)}/{a.vigenteDesde.slice(5, 7)}
                    </small>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}

        {erro && (
          <div className="aviso conflito" role="alert">
            <Icone nome="info" />
            <div>{erro}</div>
          </div>
        )}
      </CorpoDeModal>
    </Modal>
  );
}
